import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemIcon, ListItemText, Tooltip, Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFileOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ENV from "../../../config/env";
import { comprimirImagen } from "../../../utils/comprimirImagen";
import { eliminarArchivoNota, formatearPeso, listarArchivosNota, mensajeError, subirArchivosNota } from "../services/notasService";

// Archivos de una nota: ver/abrir, subir más y eliminar.
export default function NotaArchivosDialog({ nota, onClose, onCambio }) {
  const [archivos, setArchivos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState("");
  const [confirmarId, setConfirmarId] = useState(null);
  const inputRef = useRef(null);

  const cargar = useCallback(async () => {
    if (!nota) return;
    try {
      const { data } = await listarArchivosNota(nota.id);
      setArchivos(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(mensajeError(e, "No se pudieron cargar los archivos."));
    } finally {
      setCargando(false);
    }
  }, [nota]);

  useEffect(() => {
    setCargando(true);
    setError("");
    setConfirmarId(null);
    cargar();
  }, [cargar]);

  const subir = async (e) => {
    const lista = Array.from(e.target.files || []);
    e.target.value = "";
    if (!lista.length) return;
    setTrabajando(true);
    setError("");
    try {
      const listos = await Promise.all(lista.slice(0, 10).map(async (f) => (f.type.startsWith("image/") ? (await comprimirImagen(f)).file : f)));
      await subirArchivosNota(nota.id, listos);
      await cargar();
      onCambio?.();
    } catch (err) {
      setError(mensajeError(err, "No se pudieron subir los archivos."));
    } finally {
      setTrabajando(false);
    }
  };

  const eliminar = async (id) => {
    setTrabajando(true);
    setError("");
    try {
      await eliminarArchivoNota(id);
      setConfirmarId(null);
      await cargar();
      onCambio?.();
    } catch (err) {
      setError(mensajeError(err, "No se pudo eliminar el archivo."));
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <Dialog open={!!nota} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 17, fontWeight: 800 }}>
        Archivos de la nota {nota?.id}
        <Typography component="div" variant="caption" color="text.secondary">{nota ? `${nota.destinatario}${nota.referencia ? ` · ${nota.referencia}` : ""}` : ""}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError("")}>{error}</Alert>}
        {cargando ? (
          <Box sx={{ p: 3, display: "grid", placeItems: "center" }}><CircularProgress size={26} /></Box>
        ) : !archivos.length ? (
          <Box sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
            <UploadFileIcon sx={{ fontSize: 36, opacity: 0.5 }} />
            <Typography variant="body2">Esta nota todavía no tiene archivos.</Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {archivos.map((a) => (
              <ListItem
                key={a.id}
                divider
                secondaryAction={
                  confirmarId === a.id ? (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Button size="small" color="error" variant="contained" disabled={trabajando} onClick={() => eliminar(a.id)}>Eliminar</Button>
                      <Button size="small" onClick={() => setConfirmarId(null)}>No</Button>
                    </Box>
                  ) : (
                    <Box>
                      <Tooltip title="Abrir"><IconButton size="small" component="a" href={`${ENV.API_BASE_URL}${a.archivo_url}`} target="_blank" rel="noopener noreferrer"><OpenInNewIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Eliminar"><IconButton size="small" color="error" disabled={trabajando} onClick={() => setConfirmarId(a.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  )
                }
              >
                <ListItemIcon sx={{ minWidth: 34 }}><InsertDriveFileIcon fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary={a.nombre_original}
                  secondary={`${formatearPeso(a.tamano)} · ${a.usuario || "-"} · ${String(a.fecha || "").slice(0, 10)}`}
                  slotProps={{ primary: { noWrap: true, sx: { fontSize: 13.5, pr: 9 } }, secondary: { sx: { fontSize: 11.5 } } }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <input ref={inputRef} type="file" multiple style={{ display: "none" }} onChange={subir} />
        <Button startIcon={trabajando ? <CircularProgress size={16} /> : <AttachFileIcon />} disabled={trabajando} onClick={() => inputRef.current?.click()}>Subir archivos</Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
